import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Search, TrendingUp, TrendingDown, Send, CreditCard, Zap, FileText } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { transactionService } from '../../services/TransactionService';
import type { Transaction } from '../../lib/supabase';

const TransactionHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const [filter, setFilter] = useState<'all' | 'completed' | 'pending' | 'failed' | 'refunded'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSpent: 0,
    totalTransactions: 0,
    completedTransactions: 0,
    failedTransactions: 0,
    refundedTransactions: 0
  });

  useEffect(() => {
    if (user) {
      loadTransactions();
      loadStats();
    }
  }, [user]);

  const loadTransactions = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const data = await transactionService.getUserTransactions(user.id);
      setTransactions(data);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!user) return;

    try {
      const data = await transactionService.getTransactionStats(user.id);
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesFilter = filter === 'all' || transaction.status === filter;
    const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.payment_method.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.product_type.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getTransactionIcon = (productType: string) => {
    switch (productType) {
      case 'premium_subscription': return <Zap className="text-purple-500" size={20} />;
      case 'super_like': return <TrendingUp className="text-pink-500" size={20} />;
      case 'boost': return <TrendingUp className="text-orange-500" size={20} />;
      case 'gift': return <Send className="text-blue-500" size={20} />;
      case 'verification': return <FileText className="text-green-500" size={20} />;
      default: return <CreditCard className="text-gray-500" size={20} />;
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'pi_network': return '🥧';
      case 'stripe': return '💳';
      case 'apple_pay': return '🍎';
      case 'google_pay': return '🟢';
      case 'credit_card': return '💳';
      default: return '💰';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'pending': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'failed': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
      case 'refunded': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatProductType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const exportTransactions = () => {
    const csvContent = [
      ['Date', 'Product Type', 'Amount', 'Currency', 'Payment Method', 'Description', 'Status'].join(','),
      ...filteredTransactions.map(t => [
        formatDate(t.created_at),
        t.product_type,
        t.amount,
        t.currency,
        t.payment_method,
        t.description,
        t.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pi-dating-transactions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen">
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/payment-methods')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Transaction History</h1>
      </div>

      <div className="px-4 pb-8 space-y-4">
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
              <Button
                onClick={exportTransactions}
                variant="outline"
                size="sm"
                className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
              >
                <Download size={16} className="mr-1" />
                Export
              </Button>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-3 text-center">
            <div className="flex justify-center mb-2">
              <CreditCard className="text-blue-500" size={20} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Total Spent</p>
            <p className="font-bold text-gray-800 dark:text-gray-100">π {stats.totalSpent.toFixed(2)}</p>
          </Card>
          <Card className="p-3 text-center">
            <div className="flex justify-center mb-2">
              <TrendingUp className="text-green-500" size={20} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Completed</p>
            <p className="font-bold text-gray-800 dark:text-gray-100">{stats.completedTransactions}</p>
          </Card>
          <Card className="p-3 text-center">
            <div className="flex justify-center mb-2">
              <TrendingDown className="text-red-500" size={20} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Failed</p>
            <p className="font-bold text-gray-800 dark:text-gray-100">{stats.failedTransactions}</p>
          </Card>
          <Card className="p-3 text-center">
            <div className="flex justify-center mb-2">
              <Send className="text-purple-500" size={20} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">Total</p>
            <p className="font-bold text-gray-800 dark:text-gray-100">{stats.totalTransactions}</p>
          </Card>
        </div>

        <div className="space-y-3">
          {loading ? (
            <Card className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">Loading transactions...</p>
            </Card>
          ) : filteredTransactions.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">No transactions found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                Your payment history will appear here once you make a purchase
              </p>
            </Card>
          ) : (
            filteredTransactions.map((transaction) => (
              <Card key={transaction.id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {getTransactionIcon(transaction.product_type)}
                      <span className="text-xl">{getPaymentMethodIcon(transaction.payment_method)}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                        {transaction.description || formatProductType(transaction.product_type)}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <span>{formatProductType(transaction.payment_method)}</span>
                        <span>•</span>
                        <span>{formatDate(transaction.created_at)}</span>
                      </div>
                      {transaction.payment_method_last4 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>****{transaction.payment_method_last4}</span>
                        </div>
                      )}
                      {(transaction.failed_reason || transaction.refund_reason) && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {transaction.failed_reason || transaction.refund_reason}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-gray-800 dark:text-gray-100">
                        {transaction.currency} {Number(transaction.amount).toFixed(2)}
                      </span>
                    </div>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getStatusColor(transaction.status)}`}>
                      {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                    </span>
                    {transaction.receipt_url && (
                      <a
                        href={transaction.receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 dark:text-blue-400 block mt-1 hover:underline"
                      >
                        View Receipt
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionHistoryPage;
